/**
 * Authentication Helpers (v1.2 2026-10-07, T534: mf_signed_in_before marker; v1.1 2026-09-30, cc_tasks row 322 / T356: no signed-out profiles lookup
 *   and no insert into the missing login_history table; both only produced console errors on /auth)
 * WHY: Centralized auth logic for profile management, session handling, and auth method detection
 */

import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import { getMyTier, tierRank } from "@/lib/userTier";

// Auth method types for returning user detection
export type AuthMethod = "google" | "linkedin" | "email" | "magic_link";

const LAST_AUTH_METHOD_KEY = "brand_monitor_last_auth_method";
const LAST_EMAIL_KEY = "brand_monitor_last_email";

/**
 * Check if a user profile exists for the given email
 * WHY: Smart returning user detection - show personalized welcome message
 */
export async function checkExistingUser(email: string): Promise<{
  exists: boolean;
  lastAuthMethod?: AuthMethod;
}> {
  // First check localStorage for returning user
  const storedEmail = localStorage.getItem(LAST_EMAIL_KEY);
  const storedMethod = localStorage.getItem(LAST_AUTH_METHOD_KEY) as AuthMethod | null;

  if (storedEmail === email && storedMethod) {
    return { exists: true, lastAuthMethod: storedMethod };
  }

  // T356 (cc_tasks row 322): no database lookup. This runs on the sign-in page, where the
  // visitor is signed out, and profiles is readable only by its owner, so the old
  // profiles query was refused (401, "permission denied for table profiles") on every
  // email blur and printed an error in every visitor's console. It could never answer,
  // and if it ever could it would tell anyone whether an address has an account.
  return { exists: false };
}

/**
 * Store the last successful auth method for returning user detection
 * WHY: Personalize the login experience by highlighting preferred auth method
 */
export function storeLastAuthMethod(email: string, method: AuthMethod): void {
  localStorage.setItem(LAST_EMAIL_KEY, email);
  localStorage.setItem(LAST_AUTH_METHOD_KEY, method);
  // T534: this browser has signed in here; kept at sign-out, so /auth opens on Sign in next time.
  localStorage.setItem("mf_signed_in_before", "1");
}

/**
 * Clear stored auth method (for logout)
 */
export function clearStoredAuthMethod(): void {
  localStorage.removeItem(LAST_EMAIL_KEY);
  localStorage.removeItem(LAST_AUTH_METHOD_KEY);
}

/**
 * Ensure a profile exists for the authenticated user
 * WHY: Profiles are auto-created by trigger, but this is a safety net
 * Also updates last_login_at timestamp
 */
export async function ensureProfile(user: User): Promise<{
  isNewUser: boolean;
  onboardingCompleted: boolean;
  tier: string;
  error?: string;
}> {
  try {
    // Check if profile exists
    const { data: existingProfile, error: fetchError } = await supabase
      .from("profiles")
      .select("id, onboarding_complete, last_login_at, persistent_login")
      .eq("id", user.id)
      .maybeSingle();

    if (fetchError) {
      console.error("Error fetching profile:", fetchError);
      return {
        isNewUser: false,
        onboardingCompleted: false,
        tier: "lite",
        error: fetchError.message,
      };
    }

    // Profile should exist from trigger, but handle edge case
    if (!existingProfile) {
      // This shouldn't happen with our trigger, but create profile as fallback
      const { error: insertError } = await supabase.from("profiles").insert({
        id: user.id,
        email: user.email || "",
        full_name: user.user_metadata?.full_name || null,
        company_name: null,
        // No tier here. The tier lives in user_credits_v2 and handle_new_user
        // provisions it; writing 'pro' at signup is exactly what put 13 of 18
        // accounts on a tier nothing ever wrote back.
        trial_ends_at: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
        onboarding_completed: false,
        email_opt_in: true,
      });

      if (insertError) {
        console.error("Error creating profile:", insertError);
        return {
          isNewUser: true,
          onboardingCompleted: false,
          tier: "pro",
          error: insertError.message,
        };
      }

      return {
        isNewUser: true,
        onboardingCompleted: false,
        tier: "pro",
      };
    }

    // Update last_login_at. T356 (cc_tasks row 322): the login_history insert that sat beside
    // this is gone. That table does not exist, so the insert failed (404) and logged an error
    // on every sign-in; nothing read what it would have written.
    const { error: lastLoginErr } = await supabase
      .from("profiles")
      .update({ last_login_at: new Date().toISOString() })
      .eq("id", user.id);
    if (lastLoginErr) console.warn("[ensureProfile] last_login_at not saved:", lastLoginErr.message);

    // Apply persistent_login preference
    const persistentLogin = (existingProfile as any).persistent_login ?? true;
    if (!persistentLogin) {
      window.localStorage.setItem("mf_session_ephemeral", "true");
    } else {
      window.localStorage.removeItem("mf_session_ephemeral");
    }

    // Determine if this is effectively a new user (first login or no last_login)
    const isNewUser = !existingProfile.last_login_at;

    return {
      isNewUser,
      onboardingCompleted: (existingProfile as any).onboarding_complete || false,
      tier: existingProfile.tier || "lite",
    };
  } catch (error) {
    console.error("Error in ensureProfile:", error);
    return {
      isNewUser: false,
      onboardingCompleted: false,
      tier: "lite",
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

/**
 * Check if user's trial has expired
 * WHY: Gate features for expired trial users
 */
export async function checkTrialStatus(userId: string): Promise<{
  isExpired: boolean;
  tier: string;
  trialEndsAt: Date | null;
}> {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("trial_ends_at")
      .eq("id", userId)
      .single();

    if (error || !data) {
      return { isExpired: false, tier: "free", trialEndsAt: null };
    }

    const tier = await getMyTier();
    const trialEndsAt = data.trial_ends_at ? new Date(data.trial_ends_at) : null;
    const isExpired = trialEndsAt ? trialEndsAt < new Date() : false;

    return {
      isExpired: isExpired && tierRank(tier) < tierRank("pro"),
      tier,
      trialEndsAt,
    };
  } catch {
    return { isExpired: false, tier: "lite", trialEndsAt: null };
  }
}

/**
 * Get auth method display name
 */
export function getAuthMethodLabel(method: AuthMethod): string {
  switch (method) {
    case "google":
      return "Google";
    case "linkedin":
      return "LinkedIn";
    case "email":
      return "email and password";
    case "magic_link":
      return "magic link";
    default:
      return "email";
  }
}
